/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Spacer, VisuallyHidden, ZapierLogo } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import {
  FALLBACK_TOP_NAV_RESPONSE,
  UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
  UNIVERSAL_TOPBAR_HEIGHT,
  skipLinkHref as skipLinkHrefDefault,
} from '../../constants';

import { useUniversalNavContext } from '../../hooks';
import SkipLink from '../SkipLink';
import UniversalTopbarLink from '../UniversalTopbar/UniversalTopbarLink';
import type { TopbarProps } from '../UniversalTopbar/types';

import AccountActions from '../UniversalTopbar/AccountActions';
import SearchForm from '../UniversalTopbar/SearchForm';

import type { Props as SearchFormProps } from '../UniversalTopbar/SearchForm';

export type Props = TopbarProps & {
  shouldRenderHiddenMenu?: boolean;
};

const Styles = labelStyles('TopbarLoadingState', {
  root: [
    {
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: `var(--zds-background-weaker, #FFFDF9)`,
      position: 'relative', // Needed for submenu dropdown
      borderBottom: `1px solid var(--zds-gray-warm-3, #ECE9DF)`,
    },
  ],
  header: css({
    width: '100%',
    height: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
    padding: '0 10px 0 59px',
    display: 'grid',
    gridTemplateColumns: 'max-content auto max-content',
    alignItems: 'stretch',
    gap: '5px',
    backgroundColor: 'var(--zds-background-weaker, #FFFDF9)',

    '@media (min-width: 450px)': {
      height: UNIVERSAL_TOPBAR_HEIGHT,
      padding: '0 20px 0 71px',
      gap: '0',
    },

    '@media (min-width: 1040px)': {
      paddingLeft: '20px',
    },
  }),
  group: css({
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    justifySelf: 'flex-end',
    gap: '5px',

    '@media (min-width: 450px)': {
      gap: '17px',
    },
  }),
  mainNavigationContent: [
    {
      display: 'flex',
      alignItems: 'stretch',
      marginLeft: 25,
    },
  ],
  logoLinksWrapper: {
    // This section has an element with position: absolute,
    // relative positioning here will define the bounds
    position: 'relative',
  },
  skipLink: {
    position: 'absolute',
    // Positions the SkipLink just below the logo
    top: '100%',
    whiteSpace: 'nowrap',
  },
});

/**
 * @private
 * Renders loading state for the dynamic navigation top bar with Zapier logo, search,
 * and account actions.
 */
const TopbarLoadingState = ({
  logoAriaLabel = 'Go to the Zapier Homepage',
  logoHref = '/',
  onLogoClick = () => {},
  selectedHref = '',
  shouldRenderHiddenMenu = true,
  shouldRenderLogo = true,
  size = 'medium',
  skipLinkHref = skipLinkHrefDefault,
  renderAccountMenu = (props: Props) => (
    <AccountActions
      selectedHref={props.selectedHref}
      shouldRenderSignIn={props.size !== 'small'}
      shouldRenderSignUp={true}
      size={props.size}
    />
  ),
  renderNotifications = () => null,
  renderSearchForm = (props: SearchFormProps) => (
    <SearchForm
      isSearching={props.isSearching}
      onClose={props.onClose}
      searchInputId={props.searchInputId}
    />
  ),
  ...otherProps
}: Props) => {
  const props = {
    shouldRenderHiddenMenu,
    logoAriaLabel,
    logoHref,
    onLogoClick,
    renderAccountMenu,
    renderNotifications,
    renderSearchForm,
    selectedHref,
    shouldRenderLogo,
    size,
    skipLinkHref,
    ...otherProps,
  } satisfies Props;

  const { getFullyQualifiedURL } = useUniversalNavContext();
  const logoSize = {
    width: 105,
    height: 28,
  };

  return (
    <div css={Styles.root} data-zds>
      <div
        css={Styles.header}
        data-testid="universalTopbar-loading-state"
        data-zds
      >
        <div css={Styles.group} data-zds>
          {shouldRenderLogo && (
            <header css={Styles.logoLinksWrapper} data-zds>
              <a
                aria-label={logoAriaLabel}
                href={getFullyQualifiedURL(logoHref)}
                onClick={onLogoClick}
                data-zds
              >
                <ZapierLogo {...logoSize} />
              </a>
              <div css={Styles.skipLink} data-zds>
                <SkipLink href={skipLinkHref} />
              </div>
            </header>
          )}
        </div>
        <div css={Styles.mainNavigationContent} data-zds>
          {shouldRenderHiddenMenu ? (
            <VisuallyHidden>
              <nav aria-label="Site Links" id="site-nav" data-zds>
                <ul data-zds>
                  {FALLBACK_TOP_NAV_RESPONSE?.topBarMenu?.map((link) => (
                    <UniversalTopbarLink key={link.text} navigationItem={link}>
                      {link.text}
                    </UniversalTopbarLink>
                  ))}
                </ul>
              </nav>
            </VisuallyHidden>
          ) : (
            <Spacer />
          )}
        </div>
        <div
          aria-label="Manage your account and search apps"
          css={Styles.group}
          role="form"
          data-zds
        >
          {renderAccountMenu(props)}
        </div>
      </div>
    </div>
  );
};

export default TopbarLoadingState;
